import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import {
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Chip,
  FormHelperText,
  Divider,
} from '@mui/material';
import {
  Key as KeyIcon,
  SmartToy as BotIcon,
  WhatsApp as WhatsAppIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { botService, whatsappService } from '../services/api';

const steps = [
  {
    label: 'Informações do Bot',
    icon: <BotIcon />,
  },
  {
    label: 'Configurar OpenAI',
    icon: <KeyIcon />,
  },
  {
    label: 'Conectar WhatsApp',
    icon: <WhatsAppIcon />,
  },
];

const CreateBot: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [whitelistInput, setWhitelistInput] = useState('');
  const [botData, setBotData] = useState({
    name: '',
    openaiKey: '',
    assistantType: 'existing',
    assistantId: '',
    assistantName: '',
    stopMessage: 'PARAR BOT',
    stopMessageConfirmation: '',
    resumeMessage: 'CONTINUAR BOT',
    clientStopMessage: 'PARAR',
    clientResumeMessage: 'CONTINUAR',
    clientStopConfirmation: '🛑 Atendimento automático pausado. Para reativar, envie CONTINUAR.',
    clientResumeConfirmation: '✅ Atendimento automático reativado! Como posso ajudá-lo?',
    whitelist: [] as string[],
    whitelistMessage: 'Desculpe, você não tem permissão para interagir com este bot.',
  });
  const [assistants, setAssistants] = useState<any[]>([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [createdBot, setCreatedBot] = useState<any>(null);
  const [qrCode, setQrCode] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);

  const createBotMutation = useMutation(
    (data: any) => botService.create(data),
    {
      onSuccess: (response) => {
        setCreatedBot(response.data.bot);
        setActiveStep(2);
        connectWhatsApp(response.data.bot.id);
      },
      onError: (error: any) => {
        setError(error.response?.data?.error || 'Erro ao criar bot');
      },
    }
  );

  const handleNext = () => {
    setError('');
    
    if (activeStep === 0) {
      if (!botData.name.trim()) {
        setError('Nome do bot é obrigatório');
        return;
      }
      if (!botData.stopMessage.trim()) {
        setError('Mensagem de parada é obrigatória');
        return;
      }
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (!botData.openaiKey.trim()) {
        setError('Chave OpenAI é obrigatória');
        return;
      }
      if (botData.assistantType === 'existing' && !botData.assistantId) {
        setError('Selecione um assistente');
        return;
      }
      createBotMutation.mutate({
        name: botData.name,
        openaiKey: botData.openaiKey,
        assistantId: botData.assistantType === 'existing' ? botData.assistantId : null,
        assistantName: botData.assistantType === 'existing' ? botData.assistantName : null,
        stop_message: botData.stopMessage,
        stop_message_confirmation: botData.stopMessageConfirmation,
        resume_message: botData.resumeMessage,
        client_stop_message: botData.clientStopMessage,
        client_resume_message: botData.clientResumeMessage,
        client_stop_confirmation: botData.clientStopConfirmation,
        client_resume_confirmation: botData.clientResumeConfirmation,
        whitelist: botData.whitelist.length > 0 ? botData.whitelist : null,
        whitelist_message: botData.whitelistMessage,
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError('');
  };

  const loadAssistants = async () => {
    if (!botData.openaiKey) {
      setError('Digite sua chave OpenAI primeiro');
      return;
    }

    setLoadingAssistants(true);
    setError('');

    try {
      const response = await botService.getAssistants(botData.openaiKey);
      setAssistants(response.data.assistants);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Erro ao buscar assistentes');
    } finally {
      setLoadingAssistants(false);
    }
  };

  const handleAddWhitelistNumber = () => {
    const cleanNumber = whitelistInput.replace(/\D/g, '');
    if (cleanNumber && !botData.whitelist.includes(cleanNumber)) {
      setBotData({
        ...botData,
        whitelist: [...botData.whitelist, cleanNumber],
      });
      setWhitelistInput('');
    }
  };

  const handleRemoveWhitelistNumber = (numberToRemove: string) => {
    setBotData({
      ...botData,
      whitelist: botData.whitelist.filter(num => num !== numberToRemove),
    });
  };

  const connectWhatsApp = async (botId: string) => {
    setIsConnecting(true);
    try {
      await whatsappService.connect(botId);
      
      // Polling para obter QR Code
      const checkQR = setInterval(async () => {
        try {
          const response = await whatsappService.getQR(botId);
          if (response.data.qrCode) {
            setQrCode(response.data.qrCode);
            clearInterval(checkQR);
          }
        } catch (error) {
          console.log('Aguardando QR Code...');
        }
      }, 2000);

      // Verificar status de conexão
      const checkStatus = setInterval(async () => {
        try {
          const response = await whatsappService.getStatus(botId);
          if (response.data.isConnected && response.data.phoneNumber) {
            clearInterval(checkStatus);
            clearInterval(checkQR);
            navigate(`/bots/${botId}`);
          }
        } catch (error) {
          console.log('Verificando conexão...');
        }
      }, 3000);

      // Timeout de 5 minutos
      setTimeout(() => {
        clearInterval(checkQR);
        clearInterval(checkStatus);
      }, 300000);
    } catch (error: any) {
      setError('Erro ao conectar WhatsApp');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Criar Novo Bot
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>{steps[0].label}</StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Nome do Bot"
                  value={botData.name}
                  onChange={(e) => setBotData({ ...botData, name: e.target.value })}
                  placeholder="Ex: Atendimento Automático"
                  margin="normal"
                />
                
                {/* Controle do Dono do Chip */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    👤 Controle do Dono do Chip
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configurações para VOCÊ (portador do chip) controlar o bot
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Mensagem de Parada"
                    value={botData.stopMessage}
                    onChange={(e) => setBotData({ ...botData, stopMessage: e.target.value })}
                    margin="normal"
                    required
                    helperText="Comando que VOCÊ envia para pausar o bot"
                  />

                  <TextField
                    fullWidth
                    label="Confirmação de Parada (opcional)"
                    value={botData.stopMessageConfirmation}
                    onChange={(e) => setBotData({ ...botData, stopMessageConfirmation: e.target.value })}
                    margin="normal"
                    multiline
                    rows={2}
                    helperText="Mensagem enviada ao cliente quando VOCÊ pausa o bot"
                  />
                  
                  <TextField
                    fullWidth
                    label="Mensagem de Reativação"
                    value={botData.resumeMessage}
                    onChange={(e) => setBotData({ ...botData, resumeMessage: e.target.value })}
                    margin="normal"
                    required
                    helperText="Comando que VOCÊ envia para reativar o bot"
                  />
                </Box>

                {/* Controle do Cliente */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom color="secondary">
                    💬 Controle do Cliente
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configurações para o CLIENTE controlar o atendimento automático
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Comando de Parada do Cliente"
                    value={botData.clientStopMessage}
                    onChange={(e) => setBotData({ ...botData, clientStopMessage: e.target.value })}
                    margin="normal"
                    required
                    helperText="Comando que o CLIENTE envia para pausar o bot"
                  />
                  
                  <TextField
                    fullWidth
                    label="Confirmação de Parada para Cliente"
                    value={botData.clientStopConfirmation}
                    onChange={(e) => setBotData({ ...botData, clientStopConfirmation: e.target.value })}
                    margin="normal"
                    multiline
                    rows={2}
                    helperText="Mensagem enviada quando o CLIENTE pausa o bot"
                  />
                  
                  <TextField
                    fullWidth
                    label="Comando de Reativação do Cliente"
                    value={botData.clientResumeMessage}
                    onChange={(e) => setBotData({ ...botData, clientResumeMessage: e.target.value })}
                    margin="normal"
                    required
                    helperText="Comando que o CLIENTE envia para reativar o bot"
                  />
                  
                  <TextField
                    fullWidth
                    label="Confirmação de Reativação para Cliente"
                    value={botData.clientResumeConfirmation}
                    onChange={(e) => setBotData({ ...botData, clientResumeConfirmation: e.target.value })}
                    margin="normal"
                    multiline
                    rows={2}
                    helperText="Mensagem enviada quando o CLIENTE reativa o bot"
                  />
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Whitelist de Contatos (Opcional)
                  </Typography>
                  <FormHelperText>
                    Adicione números que podem interagir com o bot. Deixe vazio para permitir todos.
                  </FormHelperText>
                  
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <TextField
                      label="Número WhatsApp"
                      value={whitelistInput}
                      onChange={(e) => setWhitelistInput(e.target.value)}
                      placeholder="5511999999999"
                      size="small"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddWhitelistNumber();
                        }
                      }}
                    />
                    <IconButton 
                      color="primary" 
                      onClick={handleAddWhitelistNumber}
                      disabled={!whitelistInput.trim()}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {botData.whitelist.map((number) => (
                      <Chip
                        key={number}
                        label={number}
                        onDelete={() => handleRemoveWhitelistNumber(number)}
                        deleteIcon={<DeleteIcon />}
                      />
                    ))}
                    {botData.whitelist.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Nenhum número na whitelist (bot atende todos)
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    fullWidth
                    label="Mensagem para Contatos Não Autorizados"
                    value={botData.whitelistMessage}
                    onChange={(e) => setBotData({ ...botData, whitelistMessage: e.target.value })}
                    margin="normal"
                    multiline
                    rows={2}
                    helperText="Mensagem enviada para contatos que não estão na whitelist"
                  />
                </Box>
              </Box>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Próximo
                </Button>
                <Button onClick={() => navigate('/bots')} sx={{ mt: 1, mr: 1 }}>
                  Cancelar
                </Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{steps[1].label}</StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Chave da API OpenAI"
                  type="password"
                  value={botData.openaiKey}
                  onChange={(e) => setBotData({ ...botData, openaiKey: e.target.value })}
                  placeholder="sk-..."
                  margin="normal"
                />
                
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                  <FormLabel component="legend">Tipo de Assistente</FormLabel>
                  <RadioGroup
                    value={botData.assistantType}
                    onChange={(e) => setBotData({ ...botData, assistantType: e.target.value })}
                  >
                    <FormControlLabel
                      value="existing"
                      control={<Radio />}
                      label="Usar assistente existente"
                    />
                    <FormControlLabel
                      value="new"
                      control={<Radio />}
                      label="Criar novo assistente"
                      disabled
                    />
                  </RadioGroup>
                </FormControl>

                {botData.assistantType === 'existing' && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={loadAssistants}
                      disabled={loadingAssistants || !botData.openaiKey}
                    >
                      {loadingAssistants ? <CircularProgress size={20} /> : 'Carregar Assistentes'}
                    </Button>
                    
                    {assistants.length > 0 && (
                      <List sx={{ mt: 2, maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider' }}>
                        {assistants.map((assistant) => (
                          <ListItem key={assistant.id} disablePadding>
                            <ListItemButton
                              selected={botData.assistantId === assistant.id}
                              onClick={() => setBotData({
                                ...botData,
                                assistantId: assistant.id,
                                assistantName: assistant.name,
                              })}
                            >
                              <ListItemText
                                primary={assistant.name || 'Sem nome'}
                                secondary={assistant.id}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                )}
              </Box>
              
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={createBotMutation.isLoading}
                  sx={{ mt: 1, mr: 1 }}
                >
                  {createBotMutation.isLoading ? <CircularProgress size={20} /> : 'Criar Bot'}
                </Button>
                <Button
                  disabled={createBotMutation.isLoading}
                  onClick={handleBack}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Voltar
                </Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{steps[2].label}</StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                {isConnecting && !qrCode && (
                  <Box textAlign="center" py={3}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      Gerando QR Code...
                    </Typography>
                  </Box>
                )}
                
                {qrCode && (
                  <Box textAlign="center">
                    <Typography variant="h6" gutterBottom>
                      Escaneie o QR Code com seu WhatsApp
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      1. Abra o WhatsApp no seu celular
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      2. Toque em Menu ou Configurações e selecione WhatsApp Web
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      3. Aponte seu telefone para esta tela para capturar o código
                    </Typography>
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <img src={qrCode} alt="QR Code" style={{ maxWidth: '300px' }} />
                    </Box>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Aguardando conexão...
                    </Typography>
                  </Box>
                )}
                
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Button onClick={() => navigate('/bots')} sx={{ mt: 1, mr: 1 }}>
                  Configurar Depois
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>
    </Container>
  );
};

export default CreateBot;